/**
 * The app's one React Query client, and the defaults every query starts from.
 *
 * One client for the whole app so that every card asking for the same thing
 * shares one request and one cache. The defaults are deliberately gentle:
 * five minutes fresh, one retry (never after a 429, see queryRetry), because nearly everything here is the
 * gardener's own garden, which changes only when they change it.
 *
 * Used by: App.tsx (QueryClientProvider), and a few modules that need the
 * client outside a component or the stale time for their own queries (the
 * header, Today, the tips, the season and frost screens).
 *
 * Surprising: no query key names the garden being tended (shared gardens).
 * Switching gardens reloads the page instead (shared/api/gardenMembers
 * chooseGarden), which empties this cache.
 *
 * @module queryClient
 */

import { QueryClient } from '@tanstack/react-query';
import { retryRule } from '@/config/queryRetry';
import { QUERY_RETRY_COUNT } from '@/config/tunables/shared';

/**
 * Default stale time for every query (5 minutes), in milliseconds. Several
 * screens reuse it for their own queries. (It was first written for the
 * planting timeline's caches; the timeline is retired, see dep/.)
 * @kept K1
 */
export const QUERY_STALE_TIME_MS = 5 * 60 * 1000;

/** The client every query in the app goes through. */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: QUERY_STALE_TIME_MS,
      retry: retryRule(QUERY_RETRY_COUNT),
    },
  },
});
